import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Pause, Play, Volume2 } from "lucide-react";
import { ASSISTANT_ROBOT } from "@/data/media";

const API = process.env.REACT_APP_BACKEND_URL;

export const NarratorRobot = () => {
  const [dati, setDati] = useState(null);
  const [stato, setStato] = useState("idle"); // idle | loading | playing | paused
  const [frase, setFrase] = useState(0);
  const [tutto, setTutto] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/narration`)
      .then((r) => r.json())
      .then(setDati)
      .catch(() => setDati(null));
  }, []);

  const frasi = dati?.frasi || [];

  const avvia = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (stato === "playing") {
      audio.pause();
      setStato("paused");
      return;
    }
    setStato("loading");
    try {
      await audio.play();
      setStato("playing");
    } catch {
      setStato("idle");
    }
  };

  const onTime = () => {
    const audio = audioRef.current;
    if (!audio?.duration || !frasi.length) return;
    const quota = audio.currentTime / audio.duration;
    setFrase(Math.min(frasi.length - 1, Math.floor(quota * frasi.length)));
  };

  return (
    <section
      className="relative overflow-hidden bg-[#3E1B36] py-16 sm:py-20"
      data-testid="narrator-section"
      aria-label="Guida vocale alla pagina"
    >
      <div className="pointer-events-none absolute -right-24 top-0 h-[360px] w-[360px] rounded-full bg-[#E0708A]/25 blur-[120px]" />

      <div className="relative mx-auto grid max-w-[1200px] items-center gap-10 px-5 sm:px-10 lg:grid-cols-[0.8fr_1.2fr]">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="relative mx-auto w-full max-w-[320px]"
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/15 bg-white/5">
            <img
              src={ASSISTANT_ROBOT}
              alt="Assistente digitale di DonnaImpresa"
              className="block h-auto w-full"
              loading="lazy"
              data-testid="narrator-image"
            />
            {stato === "playing" && (
              <span className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-end gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.span
                    key={i}
                    animate={{ height: [6, 20, 6] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.12 }}
                    className="w-1.5 rounded-full bg-[#E0708A]"
                  />
                ))}
              </span>
            )}
          </div>
        </motion.div>

        <div>
          <p className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#E0708A]">
            <Volume2 size={13} /> Guida vocale
          </p>
          <h2 className="mt-5 font-display text-3xl leading-tight text-white sm:text-4xl">
            Te lo spiego io, in un minuto
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/70">
            Sono l'assistente di DonnaImpresa. Premi play: ti racconto cos'è questa
            rete, cosa trovi qui e come puoi entrare anche tu. Se preferisci
            leggere, il testo scorre qui sotto.
          </p>

          <button
            type="button"
            onClick={avvia}
            disabled={!dati}
            data-testid="narrator-play"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#E0708A] px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-[#5E2A52] disabled:opacity-50"
          >
            {stato === "loading" ? (
              <Loader2 size={18} className="animate-spin" />
            ) : stato === "playing" ? (
              <Pause size={18} />
            ) : (
              <Play size={18} />
            )}
            {stato === "playing"
              ? "Metti in pausa"
              : stato === "paused"
              ? "Riprendi la guida"
              : "Ascolta la guida"}
            {dati?.durata ? (
              <span className="text-xs font-normal opacity-80">
                ({dati.durata})
              </span>
            ) : null}
          </button>

          <Link
            to="/racconta-la-tua-storia"
            data-testid="narrator-cta"
            className="group mt-6 flex w-fit items-center gap-2 text-sm font-semibold text-white transition-colors duration-300 hover:text-[#E0708A]"
          >
            Oppure vai subito al modulo
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <div
            className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6"
            data-testid="narrator-transcript"
          >
            {frasi.length ? (
              <ul className="space-y-3">
                {(tutto || stato === "playing" ? frasi : frasi.slice(0, 3)).map((f, i) => (
                  <li
                    key={i}
                    className={`text-sm leading-relaxed transition-colors duration-500 ${
                      stato === "playing" && i === frase
                        ? "text-white"
                        : "text-white/45"
                    }`}
                  >
                    {f}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-white/45">Caricamento della guida…</p>
            )}
            {frasi.length > 3 && !tutto && stato !== "playing" ? (
              <button
                type="button"
                onClick={() => setTutto(true)}
                data-testid="narrator-more"
                className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#E0708A] transition-colors duration-300 hover:text-white"
              >
                Leggi tutto il testo
              </button>
            ) : null}
          </div>

          <audio
            data-testid="narrator-audio"
            ref={audioRef}
            src={`${API}/api/narration.mp3`}
            preload="none"
            onTimeUpdate={onTime}
            onEnded={() => {
              setStato("idle");
              setFrase(0);
            }}
          />
        </div>
      </div>
    </section>
  );
};
